import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Calendar,
  Users,
  User,
  Mail,
  FileText as FileTextIcon,
  AlertTriangle,
  Download,
} from "lucide-react";
import { Employee, Plan, Enrollment, Dependent } from "@shared/schema";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { apiRequest } from "@/lib/queryClient";
import { messagesService } from "@/lib/messagesService";
import { useLocation } from "wouter";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { sortEmployeesByFullName } from "@/lib/employeeSort";
import { formatHeadcountBreakdown, getEmployeeHeadcount, isCountedInHeadcount } from "@/lib/employeeHeadcount";
import { formatDateDisplay } from "@/lib/utils";
import { planYearsForOpenEnrollment } from "@shared/utils/planYearLinks";
import { planTypeRank } from "@shared/schemas/planTypes";
import { usePlanPremiumData } from "@/hooks/usePlanPremiumData";
import { openEnrollmentPlanNotReadyReason } from "@/lib/openEnrollmentPlanReadiness";
import { useLifeDisabilityConfigs } from "@/hooks/useLifeDisabilityConfigs";
import { isLifeDisabilityType } from "@shared/schemas/plans";
import { PLAN_TYPE_OPTIONS } from "@shared/schemas/plans";
import { exportToCSV } from "@/lib/exportUtils";

interface OpenEnrollmentProps {
  clientEmployees: Employee[];
  clientId?: string;
  prospectId?: string;
  plansData: Plan[];
  /**
   * Every plan year the client has (a renewed plan has its current and its
   * renewal year). The plan picker works from these so it can leave out the
   * plan years that are expiring; falls back to plansData.
   */
  planYearsData?: Plan[];
  /** plansData still loading: show a loading state, not "selected but not found". */
  plansLoading?: boolean;
  readOnly?: boolean;
}

const OpenEnrollment = ({
  clientEmployees,
  clientId,
  prospectId,
  plansData,
  planYearsData,
  plansLoading = false,
  readOnly = false,
}: OpenEnrollmentProps) => {
  const entityId = clientId || prospectId;
  const queryClient = useQueryClient();
  // OE population = headcount (active + pending activation), same as the client
  // header (DEV-329). Total, status buckets, rows and reminders all use it, so
  // terminated/inactive employees can't inflate Not Started (DEV-358).
  const sortedClientEmployees = useMemo(() => {
    return sortEmployeesByFullName(clientEmployees.filter(isCountedInHeadcount));
  }, [clientEmployees]);
  const headcount = useMemo(() => getEmployeeHeadcount(clientEmployees), [clientEmployees]);
  const [, setLocation] = useLocation();
  const clientIdNum = useMemo(() => parseInt(entityId || "0"), [entityId]);
  const { data: clientEnrollments = [] } = useQuery<Enrollment[]>({
    queryKey: ["/api/enrollments", entityId],
    queryFn: async () => {
      const queryParam = clientId ? `clientId=${clientId}` : `prospectId=${prospectId}`;
      const res = await fetch(`/api/enrollments?${queryParam}`);
      if (!res.ok) return [] as Enrollment[];
      return res.json();
    },
    enabled: !!entityId,
  });
  const { data: windowData } = useQuery<any>({
    queryKey: clientId ? ["/api/clients", entityId, "open-enrollment"] : ["/api/prospects", entityId, "open-enrollment"],
    queryFn: async () => {
      if (prospectId) {
        // For prospects, open enrollment is not yet configured - return null
        return null;
      }
      const res = await fetch(`/api/clients/${clientId}/open-enrollment`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!entityId,
  });

  const [isEditing, setIsEditing] = useState(false);
  const canEdit = !readOnly;
  const [startDate, setStartDate] = useState<string>(
    windowData?.startDate || ""
  );
  const [endDate, setEndDate] = useState<string>(windowData?.endDate || "");
  const [selectedPlans, setSelectedPlans] = useState<number[]>(
    windowData?.selectedPlanIds || []
  );

  // View modal state
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewEmployee, setViewEmployee] = useState<Employee | null>(null);
  const [viewEnrollments, setViewEnrollments] = useState<Enrollment[]>([]);
  const [viewDependents, setViewDependents] = useState<Dependent[]>([]);

  React.useEffect(() => {
    if (windowData) {
      setStartDate(windowData.startDate || "");
      setEndDate(windowData.endDate || "");
      setSelectedPlans(windowData.selectedPlanIds || []);
      setIsEditing(false);
    }
  }, [windowData]);

  // Plans offered in the picker. Deliberately does NOT require coverage to have
  // started: open enrollment runs before the plan year begins, and the server
  // (PUT /api/clients/:clientId/open-enrollment) requires coverage to start on
  // or after the enrollment deadline. Excluding future starts here hid exactly
  // the plans the server demands.
  // Uses this client's link dates from useClientPlans, not the library plan's
  // availableStartDate/availableEndDate — those say nothing about this
  // employer's plan year.
  // Plan years that are on their way out are left off: ones that were renewed
  // (employees elect the renewal) and current plans that end before the next
  // plan year starts. A renewed plan shows with its renewal year's dates.
  // Ordered by line of coverage, then name.
  const eligiblePlans = useMemo(() => {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const offered = planYearsForOpenEnrollment(
      ((planYearsData ?? plansData) || []).filter((p: any) => (p?.isActive ?? true) !== false) as any[],
      (p: any) => Number(p.id),
      today,
    );
    // A plan already saved on this window stays listed so it can be unchecked.
    const offeredIds = new Set(offered.map((p: any) => Number(p.id)));
    const stillSelected = (plansData || []).filter(
      (p: any) => selectedPlans.includes(p.id) && !offeredIds.has(Number(p.id)),
    );
    return [...offered, ...stillSelected].sort(
      (a: any, b: any) =>
        planTypeRank(a.type) - planTypeRank(b.type) || String(a.name).localeCompare(String(b.name)),
    );
  }, [plansData, planYearsData, selectedPlans]);

  // Plan readiness uses the same loaders the employee wizard prices from, so
  // "ready" here means employees can be priced (openEnrollmentPlanReadiness.ts).
  const eligiblePlanIds = useMemo(() => eligiblePlans.map((p: any) => Number(p.id)), [eligiblePlans]);
  const {
    ratesByPlanId,
    contribsByPlanId,
    ratingAreaGapsByPlanId,
    ratingAreaUnresolvedByPlanId,
    isLoading: premiumDataLoading,
  } = usePlanPremiumData(eligiblePlanIds, clientId);
  // Life/disability plans also need their coverage setup (coverage amount,
  // composite rate) — same loader the employee wizard now uses.
  const lifeDisabilityPlanIds = useMemo(
    () => eligiblePlans.filter((p: any) => isLifeDisabilityType(String(p.type || ""))).map((p: any) => Number(p.id)),
    [eligiblePlans],
  );
  const { ldConfigByPlanId, isLoading: ldConfigsLoading } = useLifeDisabilityConfigs(lifeDisabilityPlanIds);
  // Why a plan can't be included yet, or null when it's ready. Feeds the
  // disabled checkbox, the amber styling, the tooltip and "Select all ready".
  // Never flag while loading: empty maps then are indistinguishable from "no rows".
  const planNotReadyReason = (plan: any): string | null =>
    premiumDataLoading || ldConfigsLoading
      ? null
      : openEnrollmentPlanNotReadyReason({
          plan,
          ratesByPlanId,
          contribsByPlanId,
          ratingAreaGapsByPlanId,
          ratingAreaUnresolvedByPlanId,
          ldConfigByPlanId,
        });

  const eligiblePlanGroups = useMemo(() => {
    const groups: Array<{ type: string; label: string; plans: any[] }> = [];
    for (const p of eligiblePlans as any[]) {
      const type = String(p.type || "").toLowerCase();
      const last = groups[groups.length - 1];
      if (last && last.type === type) last.plans.push(p);
      else {
        groups.push({
          type,
          label: PLAN_TYPE_OPTIONS.find((o: any) => o.value === type)?.label ?? (type || "Other"),
          plans: [p],
        });
      }
    }
    return groups;
  }, [eligiblePlans]);

  // Get selected plans details for display
  const selectedPlansDetails = useMemo(() => {
    const selectedIds = windowData?.selectedPlanIds || [];
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) return [];
    return (plansData || []).filter((p: any) => 
      selectedIds.includes(p.id)
    );
  }, [windowData?.selectedPlanIds, plansData]);

  const plansWithoutCoverageStart = useMemo(() => {
    if (!windowData?.endDate) return [] as any[];
    const enrollmentDeadline = new Date(String(windowData.endDate).split("T")[0] + "T00:00:00");
    if (Number.isNaN(enrollmentDeadline.getTime())) return [] as any[];
    return selectedPlansDetails.filter((plan: any) => {
      const coverageStarts = [plan.effectiveDate, plan.renewalDate]
        .filter(Boolean)
        .map((d: any) => new Date(d));
      return !coverageStarts.some(
        (d) => !Number.isNaN(d.getTime()) && d >= enrollmentDeadline,
      );
    });
  }, [selectedPlansDetails, windowData?.endDate]);

  // Progress tracking key: use startDate from the OE window
  const progressKey = startDate;

  // OE progress rows (kept for reset/reminders), but status rendering will also derive from enrollments.status
  const { data: progressRows = [] } = useQuery<any[]>({
    queryKey: [
      "/api/clients",
      clientId,
      "open-enrollment",
      "progress",
      progressKey,
    ],
    enabled: Boolean(progressKey) && !!clientId, // Only for clients, not prospects
    queryFn: async () => {
      if (!progressKey || !clientId) return [] as any[];
      const res = await fetch(
        `/api/clients/${clientId}/open-enrollment/progress?startDate=${progressKey}`
      );
      if (!res.ok) return [] as any[];
      return res.json();
    },
  });

  // OE Changes Report
  type ReportPlanChange = {
    planId: number;
    planName: string;
    planType: string;
    changeType: string;
    carrierName: string | null;
    networkType: string | null;
    currentTier: string | null;
    currentTotalPremium: number | null;
    currentEmployeePremium: number | null;
    currentEmployerPremium: number | null;
    dependentsCovered: string[];
    previousPlanId: number | null;
    previousPlanName: string | null;
    previousCarrierName: string | null;
    previousNetworkType: string | null;
    previousTier: string | null;
    previousTotalPremium: number | null;
    previousEmployeePremium: number | null;
    previousEmployerPremium: number | null;
    previousDependentsCovered: string[];
    waived: boolean;
    waiverReason: string | null;
  };
  type ReportRow = {
    employeeId: number;
    employeeName: string;
    department: string;
    oeStatus: string;
    planChanges: ReportPlanChange[];
  };
  type ReportData = {
    summary: {
      totalEmployees: number;
      completed: number;
      pending: number;
      notStarted: number;
      missedDeadline: number;
      newElections: number;
      planChanges: number;
      tierChanges: number;
      rateChanges: number;
      waivers: number;
      dropped: number;
      noChanges: number;
    };
    rows: ReportRow[];
  };

  const { data: reportData } = useQuery<ReportData>({
    queryKey: [
      "/api/clients",
      clientId,
      "open-enrollment",
      "report",
      progressKey,
    ],
    enabled: Boolean(progressKey) && !!clientId,
    queryFn: async () => {
      if (!progressKey || !clientId) return null as any;
      const res = await fetch(
        `/api/clients/${clientId}/open-enrollment/report?startDate=${progressKey}`
      );
      if (!res.ok) return null;
      return res.json();
    },
  });

  // Index report rows by employeeId for O(1) lookup in table
  const reportByEmployeeId = useMemo(() => {
    const map = new Map<number, ReportRow>();
    if (reportData?.rows) {
      for (const row of reportData.rows) {
        map.set(row.employeeId, row);
      }
    }
    return map;
  }, [reportData]);

  const formatTier = (tier: string | null) => (tier || "").replace(/_/g, " ");
  const formatPremium = (val: number | null) => val != null ? `$${val.toFixed(0)}/mo` : "";

  const changeTypeBadge = (changeType: string, pc: ReportPlanChange) => {
    const config: Record<string, { label: string; className: string }> = {
      new_election: { label: "New", className: "bg-green-100 text-green-800 dark:bg-green-950/30 dark:text-green-200" },
      plan_change: { label: "Plan Change", className: "bg-blue-100 text-blue-800 dark:bg-blue-950/30 dark:text-blue-200" },
      tier_change: { label: "Tier Change", className: "bg-purple-100 text-purple-800 dark:bg-purple-950/30 dark:text-purple-200" },
      rate_change: { label: "Rate Change", className: "bg-cyan-100 text-cyan-800 dark:bg-cyan-950/30 dark:text-cyan-200" },
      waived: { label: "Waived", className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-950/30 dark:text-yellow-200" },
      dropped: { label: "Dropped", className: "bg-red-100 text-red-800 dark:bg-red-950/30 dark:text-red-200" },
      no_change: { label: "No Change", className: "bg-gray-100 text-gray-600 dark:bg-gray-800/30 dark:text-gray-400" },
    };
    const c = config[changeType] || config.no_change;

    // Build concise badge detail
    let detail = "";
    if (changeType === "plan_change") {
      const tierPart = pc.previousTier && pc.currentTier && pc.previousTier !== pc.currentTier
        ? ` | ${formatTier(pc.previousTier)} \u2192 ${formatTier(pc.currentTier)}`
        : "";
      detail = `(was: ${pc.previousPlanName})${tierPart}`;
    } else if (changeType === "tier_change") {
      detail = `${formatTier(pc.previousTier)} \u2192 ${formatTier(pc.currentTier)}`;
    } else if (changeType === "rate_change") {
      if (pc.previousTotalPremium != null && pc.currentTotalPremium != null) {
        detail = `${formatPremium(pc.previousTotalPremium)} \u2192 ${formatPremium(pc.currentTotalPremium)}`;
      }
    }

    // Build tooltip content for full before/after
    const tooltipLines: string[] = [];
    if (pc.previousPlanName || pc.previousCarrierName) {
      const prevParts = [pc.previousPlanName || ""];
      if (pc.previousCarrierName) prevParts.push(pc.previousCarrierName);
      if (pc.previousNetworkType) prevParts.push(pc.previousNetworkType);
      tooltipLines.push(`Before: ${prevParts[0]}${prevParts.length > 1 ? ` (${prevParts.slice(1).join(", ")})` : ""} - ${formatTier(pc.previousTier)}${pc.previousTotalPremium != null ? ` - ${formatPremium(pc.previousTotalPremium)}` : ""}`);
    }
    {
      const curParts = [pc.planName];
      if (pc.carrierName) curParts.push(pc.carrierName);
      if (pc.networkType) curParts.push(pc.networkType);
      tooltipLines.push(`After:  ${curParts[0]}${curParts.length > 1 ? ` (${curParts.slice(1).join(", ")})` : ""} - ${formatTier(pc.currentTier)}${pc.currentTotalPremium != null ? ` - ${formatPremium(pc.currentTotalPremium)}` : ""}`);
    }
    // Dependent changes
    const prevDeps = pc.previousDependentsCovered || [];
    const curDeps = pc.dependentsCovered || [];
    const addedDeps = curDeps.filter((d) => !prevDeps.includes(d));
    const removedDeps = prevDeps.filter((d) => !curDeps.includes(d));
    if (addedDeps.length > 0) tooltipLines.push(`Dependents added: ${addedDeps.join(", ")}`);
    if (removedDeps.length > 0) tooltipLines.push(`Dependents removed: ${removedDeps.join(", ")}`);

    const hasTooltip = tooltipLines.length > 1 || (tooltipLines.length === 1 && pc.previousPlanName);

    const badgeEl = (
      <Badge key={`${changeType}-${pc.planName}`} className={`text-[10px] px-1.5 py-0 font-normal whitespace-nowrap ${c.className}`}>
        [{c.label}] {pc.planName}{detail ? ` ${detail}` : ""}
      </Badge>
    );

    if (!hasTooltip) return badgeEl;

    return (
      <TooltipProvider key={`${changeType}-${pc.planName}-tip`}>
        <Tooltip>
          <TooltipTrigger asChild>{badgeEl}</TooltipTrigger>
          <TooltipContent className="max-w-sm">
            <div className="text-xs space-y-0.5 font-mono whitespace-pre-wrap">
              {tooltipLines.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const fmtCurrency = (val: number | null) => val != null ? `$${val.toFixed(2)}` : "";

  const handleDownloadReport = () => {
    if (!reportData?.rows) return;
    const flatRows: any[] = [];
    for (const row of reportData.rows) {
      if (row.planChanges.length === 0) {
        flatRows.push({
          employeeName: row.employeeName,
          department: row.department,
          oeStatus: row.oeStatus,
          planName: "",
          carrier: "",
          networkType: "",
          planType: "",
          changeType: "",
          currentTier: "",
          currentTotalPremium: "",
          currentEmployeePremium: "",
          currentEmployerPremium: "",
          previousPlanName: "",
          previousCarrier: "",
          previousNetworkType: "",
          previousTier: "",
          previousTotalPremium: "",
          previousEmployeePremium: "",
          previousEmployerPremium: "",
          dependentsCovered: "",
          previousDependentsCovered: "",
          waiverReason: "",
        });
      } else {
        for (const pc of row.planChanges) {
          flatRows.push({
            employeeName: row.employeeName,
            department: row.department,
            oeStatus: row.oeStatus,
            planName: pc.planName,
            carrier: pc.carrierName || "",
            networkType: pc.networkType || "",
            planType: pc.planType,
            changeType: pc.changeType.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()),
            currentTier: (pc.currentTier || "").replace(/_/g, " "),
            currentTotalPremium: fmtCurrency(pc.currentTotalPremium),
            currentEmployeePremium: fmtCurrency(pc.currentEmployeePremium),
            currentEmployerPremium: fmtCurrency(pc.currentEmployerPremium),
            previousPlanName: pc.previousPlanName || "",
            previousCarrier: pc.previousCarrierName || "",
            previousNetworkType: pc.previousNetworkType || "",
            previousTier: (pc.previousTier || "").replace(/_/g, " "),
            previousTotalPremium: fmtCurrency(pc.previousTotalPremium),
            previousEmployeePremium: fmtCurrency(pc.previousEmployeePremium),
            previousEmployerPremium: fmtCurrency(pc.previousEmployerPremium),
            dependentsCovered: pc.dependentsCovered.join("; "),
            previousDependentsCovered: (pc.previousDependentsCovered || []).join("; "),
            waiverReason: pc.waiverReason || "",
          });
        }
      }
    }
    exportToCSV(flatRows, {
      filename: `oe-changes-report-${progressKey?.split("T")[0] || "unknown"}`,
      headers: [
        "Employee Name",
        "Department",
        "OE Status",
        "Change Type",
        "Plan Type",
        // Plan: previous → current
        "Previous Plan Name",
        "Current Plan Name",
        "Previous Carrier",
        "Current Carrier",
        "Previous Network Type",
        "Current Network Type",
        // Tier: previous → current
        "Previous Tier",
        "Current Tier",
        // Premiums: previous → current
        "Previous Premium",
        "Current Premium",
        "Previous Employee Premium",
        "Current Employee Premium",
        "Previous Employer Premium",
        "Current Employer Premium",
        // Dependents: previous → current
        "Previous Dependents",
        "Current Dependents",
        "Waiver Reason",
      ],
      dataMapper: (item) => [
        item.employeeName,
        item.department,
        item.oeStatus,
        item.changeType,
        item.planType,
        item.previousPlanName,
        item.planName,
        item.previousCarrier,
        item.carrier,
        item.previousNetworkType,
        item.networkType,
        item.previousTier,
        item.currentTier,
        item.previousTotalPremium,
        item.currentTotalPremium,
        item.previousEmployeePremium,
        item.currentEmployeePremium,
        item.previousEmployerPremium,
        item.currentEmployerPremium,
        item.previousDependentsCovered,
        item.dependentsCovered,
        item.waiverReason,
      ],
      toast: { success: "OE changes report exported", error: "No data to export" },
    });
  };

  // Helper: check if an enrollment's date falls within the OE window + 3-day buffer
  const isWithinOeWindow = (enrollment: any): boolean => {
    if (!windowData?.startDate || !windowData?.endDate) return true;
    const oeStart = new Date(String(windowData.startDate).split("T")[0]);
    const oeEnd = new Date(String(windowData.endDate).split("T")[0]);
    oeEnd.setDate(oeEnd.getDate() + 3); // 3-day buffer after deadline
    const enrollmentDate = enrollment.enrollmentDate || enrollment.createdAt;
    if (!enrollmentDate) return true;
    const d = new Date(String(enrollmentDate).split("T")[0]);
    return d >= oeStart && d <= oeEnd;
  };

  // Helper: determine OE status for an employee.
  // Progress table is authoritative for complete/missed_deadline (handles late/manual entries).
  // Enrollment-based derivation supplements for pending vs not_started.
  function getEmployeeOeStatus(
    employeeId: number
  ): "complete" | "pending" | "not_started" | "missed_deadline" {
    try {
      if (!progressKey) return "not_started";
      // Progress table is authoritative for complete/missed_deadline
      const progressRow = progressRows.find((p: any) => p.employeeId === employeeId);
      if (progressRow?.status === "complete") return "complete";
      if (progressRow?.status === "missed_deadline") return "missed_deadline";
      // Derive from OE enrollments for pending vs not_started
      const rows = (clientEnrollments as any[]).filter((e) => {
        if (e.employeeId !== employeeId) return false;
        const src = String(e.enrollmentSource || "").toLowerCase();
        if (src !== "open_enrollment") return false;
        return isWithinOeWindow(e);
      });
      if (rows.length > 0) {
        const anyActive = rows.some(
          (r) => String(r.status || "").toLowerCase() === "active"
        );
        return anyActive ? "complete" : "pending";
      }
      if (progressRow?.status === "pending") return "pending";
      return "not_started";
    } catch {
      return "not_started";
    }
  }

  const resetProgressMutation = useMutation({
    mutationFn: async () => {
      if (!progressKey || !clientId) return; // Only for clients
      const res = await apiRequest(
        "POST",
        `/api/clients/${clientId}/open-enrollment/progress/reset`,
        { startDate: progressKey }
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "/api/clients",
          clientId,
          "open-enrollment",
          "progress",
          progressKey,
        ],
      });
    },
  });

  const upsertMutation = useMutation({
    mutationFn: async () => {
      if (!clientId) {
        // Open enrollment editing not supported for prospects yet
        throw new Error("Open enrollment editing is not available for prospects");
      }
      const payload = {
        startDate,
        endDate,
        selectedPlanIds: selectedPlans,
      };
      const res = await apiRequest(
        "PUT",
        `/api/clients/${clientId}/open-enrollment`,
        payload
      );
      if (!res.ok) {
        // apiRequest doesn't throw on HTTP errors — without this check a 400
        // (e.g. plan-date validation) was parsed as success and silently
        // closed the editor.
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message || "Failed to save open enrollment");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/clients", clientId, "open-enrollment"],
      });
      setIsEditing(false);
    },
  });

  // Fetch dependent counts per employee
  const [dependentCounts, setDependentCounts] = useState<
    Record<number, number>
  >({});
  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const entries = await Promise.all(
          sortedClientEmployees.map(async (emp) => {
            try {
              const res = await fetch(`/api/employees/${emp.id}/dependents`);
              if (!res.ok) return [emp.id, 0] as const;
              const list = await res.json();
              return [emp.id, Array.isArray(list) ? list.length : 0] as const;
            } catch {
              return [emp.id, 0] as const;
            }
          })
        );
        if (!cancelled) {
          const map: Record<number, number> = {};
          for (const [id, count] of entries) map[id] = count;
          setDependentCounts(map);
        }
      } catch {
        if (!cancelled) setDependentCounts({});
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [sortedClientEmployees]);

  const openViewModal = async (employee: Employee) => {
    setViewEmployee(employee);
    setIsViewOpen(true);
    setViewLoading(true);
    try {
      const [enRes, depRes] = await Promise.all([
        fetch(`/api/employees/${employee.id}/enrollments`),
        fetch(`/api/employees/${employee.id}/dependents`),
      ]);
      const allEnrollments: Enrollment[] = enRes.ok ? await enRes.json() : [];
      const allDependents: Dependent[] = depRes.ok ? await depRes.json() : [];

      // Filter to selections for this Open Enrollment period only.
      // Accept if within OE window OR effectiveDate matches progressKey (catches late/manual entries).
      const oeEnrollments = (allEnrollments || []).filter((e: any) => {
        const src = String(e?.enrollmentSource || "").toLowerCase();
        if (src !== "open_enrollment") return false;
        if (isWithinOeWindow(e)) return true;
        if (!e?.effectiveDate || !progressKey) return false;
        const eDateStr = String(e.effectiveDate).split('T')[0];
        const pkStr = progressKey.split('T')[0];
        return eDateStr === pkStr;
      });

      setViewEnrollments(oeEnrollments);
      setViewDependents(allDependents || []);
    } catch {
      setViewEnrollments([]);
      setViewDependents([]);
    } finally {
      setViewLoading(false);
    }
  };

  // For prospects, show a read-only message since open enrollment is not yet configured
  const isProspect = !clientId && !!prospectId;

  return (
    <div className="space-y-4">
      {/* Prospect-specific messaging */}
      {isProspect && (
        <Card className="border-dashed border-muted-foreground/50 bg-muted/20">
          <CardContent className="pt-6">
            <div className="text-center py-4">
              <Calendar className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <h3 className="text-lg font-medium text-muted-foreground">Open Enrollment Not Available</h3>
              <p className="text-sm text-muted-foreground mt-2">
                Open enrollment periods are configured after converting this prospect to a client.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Open Enrollment Dates - only show for clients */}
      {!isProspect && (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Open Enrollment Period
              {windowData?.selectedPlanIds && Array.isArray(windowData.selectedPlanIds) && windowData.selectedPlanIds.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {windowData.selectedPlanIds.length} {windowData.selectedPlanIds.length === 1 ? "plan" : "plans"}
                </Badge>
              )}
            </CardTitle>
            {canEdit && windowData && !isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {windowData && (!isEditing || readOnly) ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
                  Enrollment Start
                </div>
                <div className="text-lg font-bold text-blue-900 dark:text-blue-100">
                  {formatDateDisplay(windowData.startDate)}
                </div>
                <div className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                  Benefits Year{" "}
                  {windowData.startDate
                    ? windowData.startDate.split("T")[0].split("-")[0]
                    : ""}
                </div>
              </div>
              <div className="text-center p-4 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-800">
                <div className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
                  Enrollment Deadline
                </div>
                <div className="text-lg font-bold text-red-900 dark:text-red-100">
                  {formatDateDisplay(windowData.endDate)}
                </div>
                <div className="text-xs text-red-600 dark:text-red-300 mt-1">
                  11:59 PM
                </div>
              </div>

              {/* Selected Plans Section */}
              {windowData ? (
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium text-muted-foreground">
                      Plans Available for Enrollment:
                    </span>
                  </div>
                  {!Array.isArray(windowData.selectedPlanIds) || windowData.selectedPlanIds.length === 0 ? (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                          No plans selected — employees can't enroll
                        </p>
                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                          The enrollment window is set, but employees will see no plans until you click Edit and choose which plans to offer.
                        </p>
                      </div>
                    </div>
                  ) : selectedPlansDetails.length === 0 && plansLoading ? (
                    <div className="text-xs text-muted-foreground">Loading plans…</div>
                  ) : selectedPlansDetails.length === 0 ? (
                    <div className="text-xs text-muted-foreground">
                      {windowData.selectedPlanIds.length} plan{windowData.selectedPlanIds.length !== 1 ? "s" : ""} selected but not found in available plans
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedPlansDetails.map((plan: any) => (
                          <Badge
                            key={plan.id}
                            variant="outline"
                            className="text-xs bg-gray-50 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700 px-2 py-0.5 font-normal"
                          >
                            <span className="capitalize">{plan.type}</span>
                            <span className="mx-1">•</span>
                            <span className="font-medium">{plan.name}</span>
                            <span className="mx-1">•</span>
                            <span className="text-muted-foreground">
                              {plan.effectiveDate ? formatDateDisplay(plan.effectiveDate) : "No start"} - {plan.endDate ? formatDateDisplay(plan.endDate) : "No end"}
                            </span>
                          </Badge>
                        ))}
                      </div>
                      {plansWithoutCoverageStart.length > 0 && (
                        <div className="mt-2 flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700">
                          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                              {plansWithoutCoverageStart.length === 1 ? "A selected plan has" : `${plansWithoutCoverageStart.length} selected plans have`} no valid coverage start date
                            </p>
                            <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                              {plansWithoutCoverageStart.map((p: any) => p.name).join(", ")}: no effective or renewal date on or after the enrollment deadline. Employees choosing {plansWithoutCoverageStart.length === 1 ? "this plan" : "these plans"} will be blocked at the final enrollment step. Set next year's renewal date on the plan to fix this.
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : null}
            </div>
          ) : canEdit ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Enrollment Start</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs">Enrollment Deadline</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Plans included</Label>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setSelectedPlans(
                        eligiblePlans
                          .filter((p: any) => !planNotReadyReason(p))
                          .map((p: any) => p.id as number)
                      )
                    }
                  >
                    Select all ready
                  </Button>
                </div>
                <div className="space-y-3">
                {eligiblePlanGroups.map((group) => (
                <div key={group.type}>
                <p className="mb-1 text-xs font-medium text-muted-foreground">{group.label}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {group.plans.map((p: any) => {
                    const checked = selectedPlans.includes(p.id as number);
                    const notReadyReason = planNotReadyReason(p);
                    // An already-selected not-ready plan must stay uncheckable.
                    const blockChecking = !!notReadyReason && !checked;
                    return (
                      <TooltipProvider key={p.id}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <label
                              className={`flex items-center gap-2 p-2 border rounded ${
                                notReadyReason
                                  ? `bg-amber-50/50 border-amber-200 ${blockChecking ? "opacity-60 cursor-not-allowed" : ""}`
                                  : ""
                              }`}
                            >
                              <Checkbox
                                checked={checked}
                                disabled={blockChecking}
                                onCheckedChange={(val) => {
                                  const isChecked = Boolean(val);
                                  if (isChecked && notReadyReason) return;
                                  setSelectedPlans((prev) =>
                                    isChecked
                                      ? Array.from(new Set([...prev, p.id as number]))
                                      : prev.filter((id) => id !== (p.id as number))
                                  );
                                }}
                              />
                              <div className="flex flex-col">
                                <span className="text-sm flex items-center gap-1.5">
                                  {p.name}
                                  {notReadyReason && (
                                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                                  )}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {p.effectiveDate ? formatDateDisplay(p.effectiveDate) : "No start"} 
                                  {" - "}
                                  {p.endDate ? formatDateDisplay(p.endDate) : "No end"}
                                </span>
                              </div>
                            </label>
                          </TooltipTrigger>
                          {notReadyReason && (
                            <TooltipContent>
                              <p>{notReadyReason}</p>
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })}
                </div>
                </div>
                ))}
                </div>
              </div>
              {upsertMutation.isError && (
                <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span className="flex-1">
                    {(upsertMutation.error as Error)?.message ||
                      "Failed to save open enrollment"}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto shrink-0 px-2 py-1 text-red-700 hover:bg-red-100"
                    onClick={() => {
                      upsertMutation.reset();
                      setIsEditing(false);
                    }}
                  >
                    Discard changes
                  </Button>
                </div>
              )}
              <div className="flex justify-end">
                {windowData && (
                  <Button
                    variant="outline"
                    onClick={() => setIsEditing(false)}
                    className="mr-2"
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  onClick={() => upsertMutation.mutate()}
                  disabled={!startDate || !endDate}
                >
                  {windowData ? "Save Changes" : "Set Open Enrollment"}
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
      )}

      {/* Employee Progress - only show for clients */}
      {!isProspect && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Employee Enrollment Progress
          </CardTitle>
          <div className="mt-2 flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={
                !windowData || resetProgressMutation.isPending
              }
              onClick={() => resetProgressMutation.mutate()}
            >
              Reset statuses to Not Started
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!windowData}
              onClick={() => {
                const targets = sortedClientEmployees.filter((emp: any) => {
                  const row = (progressRows as any[]).find(
                    (r: any) => r.employeeId === emp.id
                  );
                  const status = String(
                    row?.status || "not_started"
                  ).toLowerCase();
                  return status !== "complete";
                });
                const emails = targets
                  .map((e: any) => e.email)
                  .filter(Boolean)
                  .join(",");
                if (emails) {
                  messagesService.compose({
                    to: emails,
                    subject: `Open Enrollment Reminder`,
                    body: `Hello,\n\nOpen Enrollment is in progress. Please log in to complete your selections.\n\nThank you!`,
                    linkTo: { type: "client", id: clientIdNum },
                  });
                }
              }}
            >
              Remind all pending/not started
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={
                !reportData?.rows?.some((r) => r.planChanges.length > 0)
              }
              onClick={handleDownloadReport}
            >
              <Download className="h-3 w-3 mr-1" />
              Download Report
            </Button>
          </div>
          <div className="flex items-center gap-4 mt-2">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-sm text-muted-foreground">Not Started</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <span className="text-sm text-muted-foreground">Pending</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-sm text-muted-foreground">Complete</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
              <span className="text-sm text-muted-foreground">Missed Deadline</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Progress Summary */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="text-center p-3 bg-muted rounded-lg" data-testid="oe-total-employees">
              <div className="text-2xl font-bold">{headcount.total}</div>
              <div className="text-xs text-muted-foreground">
                Total Employees
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {formatHeadcountBreakdown(headcount)}
              </div>
            </div>
            <div className="text-center p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="text-2xl font-bold text-green-600">
                {
                  sortedClientEmployees.filter(
                    (e) => getEmployeeOeStatus(e.id) === "complete"
                  ).length
                }
              </div>
              <div className="text-xs text-green-700 dark:text-green-300">
                Complete
              </div>
            </div>
            <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <div className="text-2xl font-bold text-yellow-600">
                {
                  sortedClientEmployees.filter(
                    (e) => getEmployeeOeStatus(e.id) === "pending"
                  ).length
                }
              </div>
              <div className="text-xs text-yellow-700 dark:text-yellow-300">
                Pending
              </div>
            </div>
            <div className="text-center p-3 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-800">
              <div className="text-2xl font-bold text-red-600">
                {
                  sortedClientEmployees.filter(
                    (e) => getEmployeeOeStatus(e.id) === "not_started"
                  ).length
                }
              </div>
              <div className="text-xs text-red-700 dark:text-red-300">
                Not Started
              </div>
            </div>
            <div className="text-center p-3 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-800">
              <div className="text-2xl font-bold text-orange-600">
                {
                  sortedClientEmployees.filter(
                    (e) => getEmployeeOeStatus(e.id) === "missed_deadline"
                  ).length
                }
              </div>
              <div className="text-xs text-orange-700 dark:text-orange-300">
                Missed Deadline
              </div>
            </div>
          </div>

          {/* Employee List */}
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium">
                      Employee
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-medium">
                      Department
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-medium">
                      Status
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-medium">
                      Changes
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-medium">
                      Last Activity
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-medium">
                      Dependents
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sortedClientEmployees.map((employee: any) => {
                    const st = getEmployeeOeStatus(employee.id);
                    const status =
                      st === "complete"
                        ? "Complete"
                        : st === "pending"
                        ? "Pending"
                        : st === "missed_deadline"
                        ? "Missed Deadline"
                        : "Not Started";
                    const statusColor =
                      status === "Complete"
                        ? "bg-green-100 text-green-800 dark:bg-green-950/20 dark:text-green-200"
                        : status === "Pending"
                        ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-950/20 dark:text-yellow-200"
                        : status === "Missed Deadline"
                        ? "bg-orange-100 text-orange-800 dark:bg-orange-950/20 dark:text-orange-200"
                        : "bg-red-100 text-red-800 dark:bg-red-950/20 dark:text-red-200";
                    const progressRow = (progressRows as any[]).find(
                      (r: any) => r.employeeId === employee.id
                    );
                    const lastActivity = progressRow?.updatedAt
                      ? formatDateDisplay(progressRow.updatedAt)
                      : "Never";

                    return (
                      <tr
                        key={employee.id}
                        className="hover:bg-muted/50 cursor-pointer"
                        onClick={() => openViewModal(employee)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                              <User className="h-4 w-4" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">
                                {employee.firstName} {employee.lastName}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {employee.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm">{employee.department}</span>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`text-xs ${statusColor}`}>
                            {status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          {status === "Complete" ? (
                            <div className="flex flex-col gap-1">
                              {(() => {
                                const empReport = reportByEmployeeId.get(employee.id);
                                if (!empReport || empReport.planChanges.length === 0) {
                                  return <span className="text-xs text-muted-foreground">-</span>;
                                }
                                return empReport.planChanges
                                  .filter((pc) => pc.changeType !== "no_change")
                                  .map((pc, idx) => (
                                    <div key={idx}>
                                      {changeTypeBadge(pc.changeType, pc)}
                                    </div>
                                  ));
                              })()}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-muted-foreground">
                            {lastActivity}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm">
                            {dependentCounts[employee.id] ?? 0} dependents
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            {status === "Not Started" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  messagesService.compose({
                                    to: employee.email || undefined,
                                    subject: `Open Enrollment Reminder: ${employee.firstName} ${employee.lastName}`,
                                    body: `Hi ${employee.firstName},\n\nOpen Enrollment is in progress. Please log in to complete your selections.\n\nThanks!`,
                                    linkTo: { type: "client", id: clientIdNum },
                                  });
                                }}
                              >
                                <Mail className="h-3 w-3 mr-1" />
                                Remind
                              </Button>
                            )}
                            {status === "Pending" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setLocation(
                                    `/clients/${clientId}/employees/${employee.id}`
                                  );
                                }}
                              >
                                <FileTextIcon className="h-3 w-3 mr-1" />
                                Review
                              </Button>
                            )}
                            {status === "Missed Deadline" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  messagesService.compose({
                                    to: employee.email || undefined,
                                    subject: `Open Enrollment Follow Up: ${employee.firstName} ${employee.lastName}`,
                                    body: `Hi ${employee.firstName},\n\nThe Open Enrollment deadline has passed and we noticed your enrollment is incomplete. Please contact us as soon as possible to discuss your options.\n\nThank you!`,
                                    linkTo: { type: "client", id: clientIdNum },
                                  });
                                }}
                              >
                                <Mail className="h-3 w-3 mr-1" />
                                Follow Up
                              </Button>
                            )}
                            {status === "Complete" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openViewModal(employee);
                                }}
                              >
                                <FileTextIcon className="h-3 w-3 mr-1" />
                                View
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* View Selected Plans & Dependents Modal */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {viewEmployee
                ? `${viewEmployee.firstName} ${viewEmployee.lastName} – Selected Plans & Dependents`
                : "Selected Plans & Dependents"}
            </DialogTitle>
          </DialogHeader>

          {!windowData ? (
            <div className="text-sm text-muted-foreground">
              No open enrollment window is configured.
            </div>
          ) : viewLoading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : viewEnrollments.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No selections found for this Open Enrollment period.
            </div>
          ) : (
            <div className="space-y-4">
              {(() => {
                // Group enrollments by plan, base employee row first
                const byPlan = new Map<
                  number,
                  { base?: Enrollment; deps: Enrollment[] }
                >();
                viewEnrollments.forEach((e) => {
                  const bucket = byPlan.get(e.planId) || { deps: [] };
                  if (e.dependentId == null) bucket.base = e;
                  else bucket.deps.push(e);
                  byPlan.set(e.planId, bucket);
                });

                const rows = Array.from(byPlan.entries());
                return rows.map(([planId, grp]) => {
                  const plan = (plansData || []).find((p) => p.id === planId);
                  const depNames = (grp.deps || []).map((d) => {
                    const dep = (viewDependents || []).find(
                      (x) => x.id === d.dependentId
                    );
                    return dep
                      ? `${dep.firstName} ${dep.lastName} (${dep.relationship})`
                      : `Dependent #${d.dependentId}`;
                  });
                  const coverageTier =
                    (grp.base as any)?.coverageTier || "employee";
                  return (
                    <div key={planId} className="p-4 border rounded-md">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">
                            {plan
                              ? `${plan.name} • ${plan.type}`
                              : `Plan #${planId}`}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Coverage tier: {coverageTier}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Effective{" "}
                          {formatDateDisplay(progressKey)}
                        </div>
                      </div>
                      <div className="mt-3 text-sm">
                        <div className="font-medium mb-1">
                          Covered dependents:
                        </div>
                        {depNames.length > 0 ? (
                          <ul className="list-disc list-inside space-y-1">
                            {depNames.map((n, idx) => (
                              <li key={idx}>{n}</li>
                            ))}
                          </ul>
                        ) : (
                          <div className="text-muted-foreground">
                            None (employee only)
                          </div>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OpenEnrollment;
